import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

const DEFAULT_PAGE_SIZES = [10, 15, 25, 50, 100];

/**
 * Pagination control. Shows "Showing X to Y of Z", an optional rows-per-page
 * picker, then Previous + windowed page numbers (first, last, current±1 with
 * ellipses) + Next.
 *
 * Pass `onPageSizeChange` to get the rows-per-page picker (matches /admin/orders).
 * Without it the control hides itself on a single page, as it always has.
 */
export default function Pagination({
  page,
  totalPages,
  onChange,
  totalItems,
  pageSize,
  onPageSizeChange,
  pageSizeOptions = DEFAULT_PAGE_SIZES,
}) {
  // The page-size picker has to stay reachable even when everything fits on one
  // page — otherwise you can drop to 10 rows and never get back to 100.
  if (totalPages <= 1 && !onPageSizeChange) return null;
  const start = totalItems != null ? (page - 1) * pageSize + 1 : null;
  const end = totalItems != null ? Math.min(page * pageSize, totalItems) : null;

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-slate-500">
      <div className="flex items-center gap-4">
        {totalItems != null ? (
          <div>Showing <span className="font-medium">{totalItems === 0 ? 0 : start}</span> to <span className="font-medium">{end}</span> of <span className="font-medium">{totalItems}</span></div>
        ) : <div />}
        {onPageSizeChange && (
          <div className="flex items-center gap-2">
            <span className="text-slate-600">Rows per page:</span>
            <Select value={String(pageSize)} onValueChange={(val) => onPageSizeChange(Number(val))}>
              <SelectTrigger className="h-8 w-[70px]"><SelectValue placeholder={String(pageSize)} /></SelectTrigger>
              <SelectContent side="top">
                {pageSizeOptions.map((size) => (
                  <SelectItem key={size} value={String(size)}>{size}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
      <div className="flex items-center gap-1">
        <Button variant="outline" size="sm" className="h-8 px-2" disabled={page === 1} onClick={() => onChange(Math.max(1, page - 1))}>Previous</Button>
        <div className="flex items-center mx-2 space-x-1">
          {Array.from({ length: totalPages }).map((_, i) => {
            const n = i + 1;
            if (n === 1 || n === totalPages || (n >= page - 1 && n <= page + 1)) {
              return (
                <Button key={n} variant={page === n ? "default" : "outline"} size="sm"
                  className={cn("h-8 w-8 p-0", page === n ? "bg-primary hover:bg-primary/90 text-white" : "text-slate-600 hover:bg-slate-100")}
                  onClick={() => onChange(n)}>{n}</Button>
              );
            }
            if (n === page - 2 || n === page + 2) return <span key={n} className="text-slate-400 px-1">...</span>;
            return null;
          })}
        </div>
        <Button variant="outline" size="sm" className="h-8 px-2" disabled={page >= totalPages} onClick={() => onChange(Math.min(totalPages, page + 1))}>Next</Button>
      </div>
    </div>
  );
}
